// Client-side propulsion model — a faithful JS port of the backend's *modeled*
// (theoretical) simulator (vecros-api .../propulsion/services/simulator.py).
//
// Running it in the browser lets the public calculator compute live with no
// backend, no account and no wait — the whole point of the public demo. It is a
// first-order eCalc-style estimate (±~15%), not a high-fidelity model; verified
// thrust-stand results stay server-side behind sign-in.
//
// Keep this in sync with the Python `simulate()` if that model changes.

const G = 9.80665;
const KT_FROM_KV = 9.5493; // Kt[N·m/A] = 9.5493 / Kv

// `qnhHpa` is sea-level pressure (QNH); station pressure is derived via the ISA
// lapse from elevation. Defaults to standard sea-level pressure.
const airDensity = (elevationM, tempC, qnhHpa = 1013.25) => {
  const p0 = qnhHpa * 100.0;
  const r = 287.05;
  const pressure = p0 * (1.0 - 2.25577e-5 * Math.max(elevationM, 0.0)) ** 5.25588;
  return pressure / (r * (tempC + 273.15));
};

// Calibration constants for the static thrust/power coefficients. These are the
// one knob that sets how realistic the modeled numbers are. The backend's
// original values (Ct=0.09+0.10λ, Cp=0.028+0.10λ) over-predict hover power ~2.5×
// (Cp for real multirotor props is ≈0.04–0.05), so hover efficiency and flight
// time read far too low. These are recalibrated so common setups land in
// realistic ranges (450-class ≈ 4:1 TWR, ~45% hover, ~12 min, ~7 g/W).
// ponytail: first-order tuning knob — the backend `simulate()` should adopt the
// same constants so client demo and server agree (see FRONTEND-REQUESTS.md).
const CT_BASE = 0.085;
const CT_LAMBDA = 0.04;
const CP_BASE = 0.016;
const CP_LAMBDA = 0.028;

// Static thrust/power coefficients from geometry, scaled by catalog constants.
const propCoeffs = (diameterIn, pitchIn, tConst, pConst) => {
  let lam = 0.5;
  if (diameterIn) lam = Math.min(Math.max(pitchIn / diameterIn, 0.2), 0.9);
  const ct = (CT_BASE + CT_LAMBDA * lam) * (tConst || 1.0);
  const cp = (CP_BASE + CP_LAMBDA * lam) * (pConst || 1.0);
  return [ct, cp];
};

// Copper resistance per metre by AWG (Ω/m). Used for extension-wire losses.
export const AWG_OHM_PER_M = {
  8: 0.00206, 10: 0.00328, 12: 0.00521, 14: 0.00828, 16: 0.01317, 18: 0.02095, 20: 0.03331,
};

// Round-trip resistance of an extension wire run (Ω). Length in mm.
const wireResistance = (awg, lengthMm) => {
  const perM = AWG_OHM_PER_M[awg];
  if (!perM || !lengthMm) return 0;
  return perM * (lengthMm / 1000) * 2; // out + return conductor
};

// Case thermal resistance (K/W) — crude size/cooling heuristic: bigger motors
// shed heat better; airflow grade scales it.
// ponytail: first-order thermal model (θ = grade·100/weight_g); replace with
// per-motor thermal data if the catalog ever carries it.
const COOLING_FACTOR = { excellent: 0.7, good: 1.0, medium: 1.3, poor: 1.7, "very poor": 2.2 };
const caseThermalRes = (weightG, cooling) =>
  ((COOLING_FACTOR[cooling] ?? 1.0) * 100.0) / Math.max(weightG || 50, 10);

// Effective pitch after a yoke-twist adjustment (degrees) applied at 75% radius.
const twistedPitch = (diameterIn, pitchIn, twistDeg) => {
  if (!twistDeg || !diameterIn || !pitchIn) return pitchIn;
  const circ = 0.75 * Math.PI * diameterIn; // circumference at 75% radius
  const angle = Math.atan(pitchIn / circ) + (twistDeg * Math.PI) / 180;
  return Math.max(circ * Math.tan(angle), 0.1);
};

// Build the solver context (motor/prop/battery constants) from catalog rows +
// airframe options. Shared by the single-point solve and the throttle sweep.
const buildCtx = (parts, opts) => {
  const { motor, propeller, esc, battery } = parts;
  const {
    num_motors,
    coaxial = false,
    cells_series = 4,
    parallel = 1,
    field_elevation_m = 0.0,
    air_temperature_c = 15.0,
    pressure_hpa,
    gear_ratio = 1,
    blades = 2,
    accessory_current_a = 0.0,
    yoke_twist_deg = 0,
    batt_wire_awg,
    batt_wire_mm = 0,
    motor_wire_awg,
    motor_wire_mm = 0,
  } = opts;

  const rho = airDensity(field_elevation_m, air_temperature_c, pressure_hpa);
  const dM = (propeller.diameter_in || 0.0) * 0.0254;
  const effPitchIn = twistedPitch(propeller.diameter_in || 0.0, propeller.pitch_in || 0.0, yoke_twist_deg);
  let [ct, cp] = propCoeffs(
    propeller.diameter_in || 0.0,
    effPitchIn,
    propeller.t_const,
    propeller.p_const
  );
  // Blade-count factor relative to a 2-blade baseline (heuristic, sub-linear).
  // ponytail: first-order — more blades → more thrust & power; refine if the
  // catalog ever carries per-blade-count coefficients.
  if (blades && blades !== 2) {
    const bf = (blades / 2) ** 0.7;
    ct *= bf;
    cp *= bf;
  }
  const k_q = (cp * rho * dM ** 5) / (2 * Math.PI * 3600.0); // Q_prop = k_q * RPM^2

  const p = Math.max(1, parallel);
  const vBus0 = cells_series * (battery.nominal_voltage_v || 3.7);
  // Parallel lowers pack R; battery extension wire adds bus resistance.
  const rBatt =
    (cells_series * (battery.resistance_ohm || 0.0)) / p + wireResistance(batt_wire_awg, batt_wire_mm);
  // Motor extension wire adds per-motor series resistance.
  const rTotal =
    (motor.resistance_ohm || 0.05) + (esc.resistance_ohm || 0.0) + wireResistance(motor_wire_awg, motor_wire_mm);

  const ctx = {
    v_bus0: vBus0,
    r_batt: rBatt,
    r_total: rTotal,
    kv: motor.kv || 1000.0,
    i0: motor.no_load_current_a || 0.0,
    k_q,
    ct,
    rho,
    d_m: dM,
    gear: Math.max(gear_ratio || 1, 0.01),
    coax_factor: coaxial ? 0.78 : 1.0,
    num_motors,
    accessory_current_a,
  };
  return { ctx, rho, vBus0, rBatt };
};

// Full-system operating point at throttle fraction t (see Python docstring for
// the single-quadratic-in-RPM derivation with battery sag folded in).
const pointAtThrottle = (t, ctx) => {
  const { kv, i0, k_q } = ctx;
  const g = ctx.gear || 1;
  const kt = KT_FROM_KV / kv;
  const rEff = ctx.r_total + t * ctx.num_motors * ctx.r_batt;

  // With a G:1 reduction the prop torque referred to the motor shaft is k_q/G^3.
  const kqMotor = k_q / (g * g * g);
  const a = (kqMotor * rEff) / kt;
  const b = 1.0 / kv;
  const c = i0 * rEff - t * ctx.v_bus0;
  if (a <= 0) return null;
  const disc = b * b - 4 * a * c;
  if (disc < 0) return null;
  const rpm = (-b + Math.sqrt(disc)) / (2 * a); // motor rpm
  if (rpm <= 0) return null;

  const propRpm = rpm / g;
  const n = propRpm / 60.0; // prop rev/s
  const current = (kqMotor * rpm * rpm) / kt + i0; // per motor
  if (current <= 0) return null;
  const iTotal = ctx.num_motors * current + ctx.accessory_current_a;
  const vBus = ctx.v_bus0 - iTotal * ctx.r_batt;
  const vMotor = current * ctx.r_total + rpm / kv;

  const thrustN = ctx.ct * ctx.rho * n * n * ctx.d_m ** 4 * ctx.coax_factor;
  const pMech = k_q * propRpm * propRpm * 2 * Math.PI * n; // prop shaft power
  const pElec = vMotor * current;
  return {
    throttle_pct: t * 100.0,
    rpm: propRpm,
    current_a: current,
    voltage_v: vMotor,
    thrust_g: (thrustN / G) * 1000.0,
    total_thrust_g: (thrustN / G) * 1000.0 * ctx.num_motors,
    total_current_a: iTotal,
    bus_voltage_v: vBus,
    system_power_w: vBus * iTotal,
    mech_power_w: pMech,
    electric_power_w: pElec,
    motor_efficiency_pct: pElec > 0 ? (pMech / pElec) * 100.0 : 0.0,
  };
};

const rnd = (v, nd) => {
  if (v === null || v === undefined || Number.isNaN(v)) return null;
  const f = 10 ** nd;
  return Math.round(v * f) / f;
};

const roundPoint = (p) =>
  p === null || p === undefined
    ? null
    : {
        throttle_pct: rnd(p.throttle_pct, 1),
        rpm: rnd(p.rpm, 0),
        current_a: rnd(p.current_a, 2),
        voltage_v: rnd(p.voltage_v, 2),
        thrust_g: rnd(p.thrust_g, 0),
        total_thrust_g: rnd(p.total_thrust_g, 0),
        electric_power_w: rnd(p.electric_power_w, 1),
        mech_power_w: rnd(p.mech_power_w, 1),
        motor_efficiency_pct: rnd(p.motor_efficiency_pct, 1),
      };

/**
 * Run the theoretical model. Mirrors the backend `simulate()` output shape so it
 * feeds SimResult / the gauges unchanged. Throws on non-convergence.
 *
 * @param {{motor,propeller,esc,battery}} parts catalog rows
 * @param {object} opts all_up_weight_g, num_motors, coaxial, cells_series,
 *                       field_elevation_m, air_temperature_c, max_discharge_pct,
 *                       accessory_current_a
 */
export const simulateModeled = (parts, opts) => {
  const { motor, propeller, esc, battery } = parts;
  const {
    all_up_weight_g,
    num_motors,
    coaxial = false,
    cells_series = 4,
    parallel = 1,
    max_discharge_pct = 80.0,
  } = opts;

  const warnings = [];

  const { ctx, rho, vBus0, rBatt } = buildCtx({ motor, propeller, esc, battery }, opts);

  const atMax = pointAtThrottle(1.0, ctx);
  if (atMax === null) {
    throw new Error("Model failed to converge at full throttle — check motor/prop specs.");
  }

  // Hover: bisection on throttle where total thrust == weight.
  let atHover = null;
  if (atMax.total_thrust_g < all_up_weight_g) {
    warnings.push("Underpowered: even at full throttle total thrust is below the all-up weight.");
  } else {
    let lo = 0.05;
    let hi = 1.0;
    for (let i = 0; i < 40; i += 1) {
      const mid = (lo + hi) / 2;
      const p = pointAtThrottle(mid, ctx);
      if (p === null) {
        lo = mid;
        // eslint-disable-next-line no-continue
        continue;
      }
      if (p.total_thrust_g < all_up_weight_g) lo = mid;
      else hi = mid;
    }
    atHover = pointAtThrottle(hi, ctx);
  }

  // Optimum motor efficiency across the throttle range.
  let bestEff = null;
  for (let i = 10; i <= 100; i += 5) {
    const p = pointAtThrottle(i / 100.0, ctx);
    if (p && (bestEff === null || p.motor_efficiency_pct > bestEff.motor_efficiency_pct)) bestEff = p;
  }

  // Battery / flight-time facts. Parallel packs (P) multiply usable capacity.
  const p = Math.max(1, parallel);
  const totalCapacityMah = (battery.capacity_mah || 0.0) * p;
  // Cold-end derate: LiPo usable capacity drops when cold-soaked below ~15°C.
  // ponytail: first-order ~1%/°C below 15°C, floored at 60% — swap for a real
  // chemistry curve if the catalog ever carries one.
  const tMin = opts.temp_min_c;
  const coldFactor = tMin != null && tMin < 15 ? Math.max(0.6, 1 - 0.01 * (15 - tMin)) : 1;
  const usableMah = totalCapacityMah * (max_discharge_pct / 100.0) * coldFactor;
  const energyWh = (totalCapacityMah / 1000.0) * vBus0;
  let hoverTimeMin = null;
  let specificThrustHover = null;
  if (atHover && atHover.total_current_a > 0) {
    hoverTimeMin = (usableMah / 1000.0 / atHover.total_current_a) * 60.0;
    if (atHover.system_power_w > 0) specificThrustHover = atHover.total_thrust_g / atHover.system_power_w;
  }

  const twr = all_up_weight_g ? atMax.total_thrust_g / all_up_weight_g : null;
  const addPayload = Math.max(atMax.total_thrust_g - all_up_weight_g, 0.0);

  // Theoretical pitch speed at max throttle: prop advances `pitch` per rev.
  const pitchM = (propeller.pitch_in || 0.0) * 0.0254;
  const pitchSpeedKmh = pitchM * (atMax.rpm / 60.0) * 3.6;

  // Max tilt: at full thrust the vertical component must still carry the weight
  // (cos θ = 1/TWR), clamped by the FCU tilt limit when one is set.
  const { tilt_limit_deg, cooling, frame_size_mm } = opts;
  let maxTiltDeg = null;
  if (twr && twr > 1) {
    maxTiltDeg = (Math.acos(1 / twr) * 180) / Math.PI;
    if (tilt_limit_deg) maxTiltDeg = Math.min(maxTiltDeg, tilt_limit_deg);
  }

  // Est. motor case temperature: ambient + waste power × case thermal resistance.
  const theta = caseThermalRes(motor.weight_g, cooling);
  const ambient = opts.air_temperature_c ?? 15;
  const tempAt = (pt) =>
    pt ? ambient + Math.max(pt.electric_power_w - pt.mech_power_w, 0) * theta : null;
  const tempHoverC = tempAt(atHover);
  const tempMaxC = tempAt(atMax);

  // Total disc area (all rotors), in dm².
  const dIn = propeller.diameter_in || 0.0;
  const discAreaDm2 = num_motors * Math.PI * (dIn * 0.0254 / 2) ** 2 * 100.0;

  // Warnings against manufacturer limits.
  if (atHover && atHover.throttle_pct > 85)
    warnings.push("High hover throttle (>85%) — little headroom for maneuvers or wind.");
  if (motor.max_current_a && atMax.current_a > motor.max_current_a)
    warnings.push(
      `Motor current at max (${atMax.current_a.toFixed(1)} A) exceeds its rating (${motor.max_current_a.toFixed(0)} A).`
    );
  if (esc.max_current_a && atMax.current_a > esc.max_current_a)
    warnings.push(
      `ESC current at max (${atMax.current_a.toFixed(1)} A) exceeds its rating (${esc.max_current_a.toFixed(0)} A).`
    );
  if (esc.cont_current_a && atHover && atHover.current_a > esc.cont_current_a)
    warnings.push(
      `ESC current at hover (${atHover.current_a.toFixed(1)} A) exceeds its continuous rating (${esc.cont_current_a.toFixed(0)} A).`
    );
  if (motor.max_power_w && atMax.electric_power_w > motor.max_power_w)
    warnings.push(
      `Motor power at max (${atMax.electric_power_w.toFixed(0)} W) exceeds its limit (${motor.max_power_w.toFixed(0)} W).`
    );
  if (totalCapacityMah && battery.c_rating_max) {
    const cDraw = atMax.total_current_a / (totalCapacityMah / 1000.0);
    if (cDraw > battery.c_rating_max)
      warnings.push(
        `Battery C-rate at max (${cDraw.toFixed(0)}C) exceeds its rating (${battery.c_rating_max.toFixed(0)}C).`
      );
  }
  if (totalCapacityMah && battery.c_rating_cont && atHover) {
    const cHover = atHover.total_current_a / (totalCapacityMah / 1000.0);
    if (cHover > battery.c_rating_cont)
      warnings.push(
        `Sustained C-rate at hover (${cHover.toFixed(0)}C) exceeds the battery's continuous rating (${battery.c_rating_cont.toFixed(0)}C).`
      );
  }
  if (tempMaxC != null && tempMaxC > 90)
    warnings.push(`Estimated motor temperature at max (${tempMaxC.toFixed(0)}°C) is dangerously hot — improve cooling or reduce load.`);
  if (coldFactor < 1)
    warnings.push(
      `Cold soak at ${tMin}°C derates usable battery capacity ~${Math.round((1 - coldFactor) * 100)}% — flight time reflects it.`
    );
  // Avionics operating ranges vs the operating environment (FC + sensors).
  (opts.avionics || []).forEach((a) => {
    if (a.temp_min_c != null && tMin != null && tMin < a.temp_min_c)
      warnings.push(`${a.name} is rated to ${a.temp_min_c}°C — your minimum is ${tMin}°C.`);
    if (a.temp_max_c != null && opts.air_temperature_c != null && opts.air_temperature_c > a.temp_max_c)
      warnings.push(`${a.name} is rated to ${a.temp_max_c}°C — your maximum is ${opts.air_temperature_c}°C.`);
  });
  if (frame_size_mm && dIn && num_motors >= 3) {
    // Adjacent motor spacing for n motors on a circle of diameter frame_size.
    const spacingMm = frame_size_mm * Math.sin(Math.PI / num_motors);
    if (dIn * 25.4 > spacingMm)
      warnings.push(
        `Propellers may overlap: ${dIn}" props need ~${Math.ceil((dIn * 25.4) / Math.sin(Math.PI / num_motors))} mm frame size for ${num_motors} motors.`
      );
  }

  return {
    source: "modeled",
    accuracy_note: "Theoretical estimate (±~15%), computed in your browser. Verify against a thrust-stand test before flight.",
    air_density_kg_m3: rnd(rho, 4),
    battery: {
      cells_series,
      parallel: p,
      pack_voltage_v: rnd(vBus0, 2),
      capacity_mah: rnd(totalCapacityMah, 0),
      usable_mah: rnd(usableMah, 0),
      energy_wh: rnd(energyWh, 1),
      resistance_ohm: rnd(rBatt, 4),
    },
    hover: roundPoint(atHover),
    max: roundPoint(atMax),
    optimum: roundPoint(bestEff),
    system: {
      all_up_weight_g,
      num_motors,
      coaxial,
      thrust_to_weight: twr ? rnd(twr, 2) : null,
      max_total_thrust_g: rnd(atMax.total_thrust_g, 0),
      add_payload_g: rnd(addPayload, 0),
      hover_throttle_pct: atHover ? rnd(atHover.throttle_pct, 1) : null,
      hover_current_a: atHover ? rnd(atHover.total_current_a, 1) : null,
      hover_flight_time_min: hoverTimeMin ? rnd(hoverTimeMin, 1) : null,
      specific_thrust_hover_g_per_w: specificThrustHover ? rnd(specificThrustHover, 2) : null,
      pitch_speed_kmh: rnd(pitchSpeedKmh, 0),
      max_tilt_deg: maxTiltDeg != null ? rnd(maxTiltDeg, 0) : null,
      est_temp_hover_c: tempHoverC != null ? rnd(tempHoverC, 0) : null,
      est_temp_max_c: tempMaxC != null ? rnd(tempMaxC, 0) : null,
      disc_area_dm2: rnd(discAreaDm2, 1),
    },
    warnings,
  };
};

/**
 * Sweep the modeled operating point across throttle for charting (eCalc-style
 * "Motor Characteristic"). Returns one row per throttle step with total thrust,
 * per-motor current, system power, RPM and motor efficiency. Modeled only.
 *
 * @param {{motor,propeller,esc,battery}} parts
 * @param {object} opts same airframe options as simulateModeled
 * @param {number} steps number of samples across 5–100% throttle
 */
export const sweepModeled = (parts, opts, steps = 20) => {
  const { ctx } = buildCtx(parts, opts);
  const rows = [];
  for (let i = 0; i <= steps; i += 1) {
    const t = 0.05 + (0.95 * i) / steps;
    const p = pointAtThrottle(t, ctx);
    if (!p) continue; // eslint-disable-line no-continue
    rows.push({
      throttle_pct: rnd(t * 100, 0),
      thrust_g: rnd(p.total_thrust_g, 0),
      thrust_per_motor_g: rnd(p.thrust_g, 0),
      current_a: rnd(p.current_a, 1),
      total_current_a: rnd(p.total_current_a, 1),
      power_w: rnd(p.system_power_w, 0),
      rpm: rnd(p.rpm, 0),
      efficiency_pct: rnd(p.motor_efficiency_pct, 1),
    });
  }
  return rows;
};
